// FOCO Score calculation and interpretation logic

export interface MonthlySummary {
  total_income: number;
  total_fixed_expenses: number;
  total_variable_expenses: number;
  total_savings: number;
  total_investments: number;
  total_debt_payments: number;
  real_cash_flow: number;
  savings_rate: number;
  debt_ratio: number;
  fixed_expense_ratio: number;
  variable_expense_ratio: number;
  foco_score: number;
}

export interface FinancialGoal {
  id: string;
  name: string;
  target_amount: number;
  current_amount: number;
  status: 'active' | 'completed' | 'paused';
}

export function calculateMonthlySummary(
  income: number,
  fixedExpenses: number,
  variableExpenses: number,
  savings: number,
  investments: number,
  debtPayments: number
): Omit<MonthlySummary, 'foco_score'> {
  const realCashFlow = income - (fixedExpenses + variableExpenses + savings + investments + debtPayments);
  const savingsRate = income > 0 ? (savings + investments) / income : 0;
  const debtRatio = income > 0 ? debtPayments / income : 0;
  const fixedExpenseRatio = income > 0 ? fixedExpenses / income : 0;
  const variableExpenseRatio = income > 0 ? variableExpenses / income : 0;

  return {
    total_income: income,
    total_fixed_expenses: fixedExpenses,
    total_variable_expenses: variableExpenses,
    total_savings: savings,
    total_investments: investments,
    total_debt_payments: debtPayments,
    real_cash_flow: realCashFlow,
    savings_rate: savingsRate,
    debt_ratio: debtRatio,
    fixed_expense_ratio: fixedExpenseRatio,
    variable_expense_ratio: variableExpenseRatio,
  };
}

export function calculateFocoScore(
  summary: Omit<MonthlySummary, 'foco_score'>,
  activeGoals: FinancialGoal[]
): number {
  let score = 0;

  // +25 points if savings_rate >= 20%
  if (summary.savings_rate >= 0.20) {
    score += 25;
  }

  // +25 points if total expenses + debt <= 80% of income
  const totalOutflow = summary.total_fixed_expenses + summary.total_variable_expenses + summary.total_debt_payments;
  if (summary.total_income > 0 && (totalOutflow / summary.total_income) <= 0.80) {
    score += 25;
  }

  // +20 points if user has 2+ active goals with >= 10% progress
  const goalsWithProgress = activeGoals.filter(
    goal => goal.status === 'active' && 
    goal.target_amount > 0 && 
    (goal.current_amount / goal.target_amount) >= 0.10
  );
  if (goalsWithProgress.length >= 2) {
    score += 20;
  }

  // +20 points if debt_ratio <= 30%
  if (summary.debt_ratio <= 0.30) {
    score += 20;
  }

  // +10 points if real_cash_flow > 0
  if (summary.real_cash_flow > 0) {
    score += 10;
  }

  return Math.min(score, 100);
}

export type FocoScoreZone = 'critical' | 'risk' | 'advance' | 'liberty';

export function getFocoScoreZone(score: number): FocoScoreZone {
  if (score <= 39) return 'critical';
  if (score <= 59) return 'risk';
  if (score <= 79) return 'advance';
  return 'liberty';
}

export function getFocoScoreLabel(zone: FocoScoreZone): string {
  const labels: Record<FocoScoreZone, string> = {
    critical: 'Zona Crítica',
    risk: 'Zona de Riesgo',
    advance: 'Zona de Avance',
    liberty: 'Zona de Libertad en Construcción',
  };
  return labels[zone];
}

export function getFocoScoreInterpretation(score: number): string {
  const zone = getFocoScoreZone(score);
  
  const interpretations: Record<FocoScoreZone, string> = {
    critical: "Estás en una ZONA CRÍTICA. Tus números muestran que la estructura actual de gastos, deudas y ahorro no es sostenible. Este es el momento de hacer cambios fuertes: recortar gastos no esenciales, frenar el aumento de deudas y diseñar un plan agresivo de orden financiero. La buena noticia es que, desde aquí, cualquier mejora se siente rápido.",
    risk: "Estás en una ZONA DE RIESGO. No estás al borde del colapso, pero tu margen de maniobra es muy pequeño. Un imprevisto fuerte podría desordenar todo de nuevo. Es clave optimizar gastos fijos, reducir deudas caras y empujar tu porcentaje de ahorro/inversión. Pequeños ajustes constantes aquí marcan una gran diferencia en los próximos meses.",
    advance: "Estás en ZONA DE AVANCE. Ya se nota que hay intención de orden y que varias decisiones financieras van por buen camino. Aún hay áreas que se pueden pulir (especialmente deudas y gastos variables), pero ya estás construyendo bases para un futuro más estable. El objetivo ahora es consolidar hábitos y elevar tu porcentaje de ahorro e inversión.",
    liberty: "Estás en ZONA DE LIBERTAD EN CONSTRUCCIÓN. Tus números muestran disciplina, orden y una clara prioridad por el ahorro y la inversión. Desde aquí, el enfoque es mantener consistencia, protegerte de retrocesos y comenzar a tomar decisiones más estratégicas de crecimiento patrimonial. Estás en el camino correcto para construir verdadera libertad financiera.",
  };
  
  return interpretations[zone];
}

export type TrafficLight = 'green' | 'yellow' | 'red';

export function getSavingsTrafficLight(savingsRate: number): TrafficLight {
  if (savingsRate >= 0.20) return 'green';
  if (savingsRate >= 0.10) return 'yellow';
  return 'red';
}

export function getDebtTrafficLight(debtRatio: number): TrafficLight {
  if (debtRatio <= 0.20) return 'green';
  if (debtRatio <= 0.35) return 'yellow';
  return 'red';
}

export function getFixedExpensesTrafficLight(fixedExpenseRatio: number): TrafficLight {
  if (fixedExpenseRatio <= 0.50) return 'green';
  if (fixedExpenseRatio <= 0.65) return 'yellow';
  return 'red';
}

export function getCombinedExpensesTrafficLight(fixedExpenseRatio: number, variableExpenseRatio: number): TrafficLight {
  const combined = fixedExpenseRatio + variableExpenseRatio;
  if (combined <= 0.55) return 'green';
  if (combined <= 0.65) return 'yellow';
  return 'red';
}

export function getCashFlowTrafficLight(cashFlow: number): TrafficLight {
  if (cashFlow > 0) return 'green';
  if (cashFlow >= -100) return 'yellow';
  return 'red';
}

export function generateRecommendations(
  summary: Omit<MonthlySummary, 'foco_score'>,
  hasActiveGoals: boolean
): string[] {
  const recommendations: string[] = [];
  const savingsPercent = (summary.savings_rate * 100).toFixed(1);
  const debtPercent = (summary.debt_ratio * 100).toFixed(1);
  const fixedPercent = (summary.fixed_expense_ratio * 100).toFixed(1);
  const variablePercent = (summary.variable_expense_ratio * 100).toFixed(1);

  // Priority 1: Critical - Negative cash flow
  if (summary.real_cash_flow < 0) {
    recommendations.push(
      `🚨 URGENTE: Este mes cerraste con un déficit de ${formatCurrency(Math.abs(summary.real_cash_flow))}. Esto significa que gastaste más de lo que ingresaste. Acción inmediata: revisa los gastos variables de esta semana y pausa cualquier gasto no esencial hasta equilibrar tu flujo.`
    );
  }

  // Priority 2: High debt burden
  if (summary.debt_ratio > 0.35) {
    recommendations.push(
      `⚠️ Tus pagos de deuda representan el ${debtPercent}% de tus ingresos (ideal: menos del 35%). Considera usar el método Bola de Nieve para atacar primero las deudas pequeñas y ganar impulso. Evita completamente nuevas deudas de consumo hasta reducir este porcentaje.`
    );
  } else if (summary.debt_ratio > 0.20 && summary.debt_ratio <= 0.35) {
    recommendations.push(
      `📊 Tu ratio de deuda está en ${debtPercent}%, dentro del rango aceptable pero con espacio de mejora. Si puedes destinar un poco más cada mes a pagar deudas, acelerarás significativamente tu camino hacia la libertad financiera.`
    );
  }

  // Priority 3: Fixed expenses too high
  if (summary.fixed_expense_ratio > 0.65) {
    recommendations.push(
      `🏠 Tus gastos fijos consumen el ${fixedPercent}% de tus ingresos (ideal: menos del 50%). Revisa: ¿Puedes renegociar tu alquiler o hipoteca? ¿Hay suscripciones que no uses? ¿Servicios que puedas optimizar? Cada reducción aquí libera dinero permanentemente.`
    );
  } else if (summary.fixed_expense_ratio > 0.50 && summary.fixed_expense_ratio <= 0.65) {
    recommendations.push(
      `🔍 Tus gastos fijos están en ${fixedPercent}%. Busca una suscripción o servicio que puedas eliminar o reducir este mes. Los pequeños ajustes en gastos fijos tienen impacto acumulativo importante.`
    );
  }

  // Priority 4: Variable expenses analysis
  if (summary.variable_expense_ratio > 0.40) {
    recommendations.push(
      `🛒 Los gastos variables representan el ${variablePercent}% de tus ingresos. Identifica las 3 categorías donde más gastaste (probablemente alimentación, entretenimiento o transporte) y establece un presupuesto semanal para cada una. Usar efectivo ayuda a ser más consciente.`
    );
  }

  // Priority 5: Savings recommendations
  if (summary.savings_rate < 0.10) {
    recommendations.push(
      `💰 Tu tasa de ahorro e inversión es ${savingsPercent}% (objetivo mínimo: 10%). Empieza pequeño: automatiza una transferencia del 5% de cada ingreso a una cuenta de ahorro separada. Una vez lo normalices, incrementa gradualmente.`
    );
  } else if (summary.savings_rate < 0.20) {
    recommendations.push(
      `📈 Estás ahorrando el ${savingsPercent}% de tus ingresos. ¡Buen progreso! El siguiente nivel es llegar al 20%. Revisa si algún gasto variable puede reducirse para acelerar tu ahorro. Cada 1% adicional hace diferencia a largo plazo.`
    );
  } else {
    recommendations.push(
      `✅ ¡Excelente! Tu tasa de ahorro del ${savingsPercent}% está en el rango ideal. Ahora enfócate en la calidad: ¿están tus ahorros generando rendimientos? Considera diversificar entre fondo de emergencia (3-6 meses de gastos) e inversiones a largo plazo.`
    );
  }

  // Priority 6: Goals recommendations
  if (!hasActiveGoals) {
    recommendations.push(
      `🎯 No tienes metas financieras activas. Las metas dan dirección a tu dinero. Crea al menos 2: una de corto plazo (fondo de emergencia o pagar una deuda específica) y una de largo plazo (inversión para retiro o casa propia). Las metas con fecha límite se cumplen más.`
    );
  }

  // Priority 7: Positive reinforcement when doing well
  if (summary.real_cash_flow > 0 && summary.savings_rate >= 0.20 && summary.debt_ratio <= 0.20) {
    recommendations.unshift(
      `🌟 ¡Felicidades! Tu situación financiera está en excelente forma: flujo positivo, buen ahorro y deuda controlada. Mantén la consistencia y considera aumentar tus inversiones o acelerar alguna meta importante.`
    );
  } else if (summary.real_cash_flow > 0 && summary.savings_rate >= 0.10) {
    recommendations.push(
      `👍 Cerraste el mes con superávit de ${formatCurrency(summary.real_cash_flow)}. ¡Bien hecho! Asegúrate de que este excedente vaya a una meta específica (ahorro, inversión o pago de deuda) y no se diluya en gastos no planificados.`
    );
  }

  return recommendations;
}

export const transactionCategories = {
  income: [
    { value: 'salary', label: 'Salario' },
    { value: 'business', label: 'Negocio propio' },
    { value: 'freelance', label: 'Freelance / Consultoría' },
    { value: 'investments_income', label: 'Rendimientos de inversiones' },
    { value: 'rental', label: 'Rentas / Alquileres' },
    { value: 'bonus', label: 'Bonos / Aguinaldo' },
    { value: 'commissions', label: 'Comisiones' },
    { value: 'dividends', label: 'Dividendos' },
    { value: 'pension', label: 'Pensión' },
    { value: 'gifts_received', label: 'Regalos recibidos' },
    { value: 'refunds', label: 'Reembolsos' },
    { value: 'other_income', label: 'Otros ingresos' },
  ],
  fixed_expense: [
    { value: 'housing', label: 'Vivienda / Renta / Hipoteca' },
    { value: 'utilities_electricity', label: 'Electricidad' },
    { value: 'utilities_water', label: 'Agua' },
    { value: 'utilities_gas', label: 'Gas' },
    { value: 'internet', label: 'Internet' },
    { value: 'phone', label: 'Teléfono / Celular' },
    { value: 'cable_streaming', label: 'Cable / Streaming' },
    { value: 'insurance_health', label: 'Seguro de salud' },
    { value: 'insurance_life', label: 'Seguro de vida' },
    { value: 'insurance_car', label: 'Seguro de auto' },
    { value: 'insurance_home', label: 'Seguro de hogar' },
    { value: 'subscriptions', label: 'Suscripciones (apps, gym, etc.)' },
    { value: 'education_tuition', label: 'Colegiatura / Universidad' },
    { value: 'childcare', label: 'Guardería / Niñera' },
    { value: 'transport_fixed', label: 'Transporte fijo (marchamo, rtv)' },
    { value: 'condominium', label: 'Cuota de condominio' },
    { value: 'domestic_help', label: 'Empleada doméstica' },
    { value: 'alimony', label: 'Pensión alimenticia' },
    { value: 'membership', label: 'Membresías (club, asociación)' },
    { value: 'other_fixed', label: 'Otros gastos fijos' },
    { value: 'rent', label: 'Alquiler' },
    { value: 'insurance', label: 'Seguros' },
    { value: 'utilities', label: 'Servicios públicos' },
  ],
  variable_expense: [
    { value: 'food_groceries', label: 'Supermercado / Abarrotes' },
    { value: 'food_restaurants', label: 'Restaurantes / Comida fuera' },
    { value: 'food_delivery', label: 'Delivery / Comida a domicilio' },
    { value: 'transport_fuel', label: 'Combustible' },
    { value: 'transport_public', label: 'Transporte público' },
    { value: 'transport_uber', label: 'Uber / Taxi' },
    { value: 'transport_parking', label: 'Parqueo' },
    { value: 'entertainment_movies', label: 'Cine / Teatro' },
    { value: 'entertainment_events', label: 'Conciertos / Eventos' },
    { value: 'entertainment_hobbies', label: 'Hobbies' },
    { value: 'entertainment_nightlife', label: 'Salidas nocturnas' },
    { value: 'health_medicine', label: 'Medicamentos' },
    { value: 'health_doctor', label: 'Consultas médicas' },
    { value: 'health_dental', label: 'Dentista' },
    { value: 'health_gym', label: 'Gimnasio / Deportes' },
    { value: 'personal_care', label: 'Cuidado personal (salon, spa)' },
    { value: 'clothing', label: 'Ropa y calzado' },
    { value: 'electronics', label: 'Electrónicos / Tecnología' },
    { value: 'home_maintenance', label: 'Mantenimiento del hogar' },
    { value: 'car_maintenance', label: 'Mantenimiento del auto' },
    { value: 'pets', label: 'Mascotas' },
    { value: 'gifts', label: 'Regalos' },
    { value: 'donations', label: 'Donaciones / Caridad' },
    { value: 'education_courses', label: 'Cursos / Capacitaciones' },
    { value: 'education_books', label: 'Libros / Material educativo' },
    { value: 'travel', label: 'Viajes / Vacaciones' },
    { value: 'household_items', label: 'Artículos para el hogar' },
    { value: 'other_variable', label: 'Otros gastos variables' },
    { value: 'beauty', label: 'Belleza / Estética' },
    { value: 'real_estate', label: 'Bienes raíces' },
    { value: 'food', label: 'Alimentación' },
    { value: 'transport', label: 'Transporte' },
    { value: 'transportation', label: 'Transporte' },
    { value: 'entertainment', label: 'Entretenimiento' },
    { value: 'health', label: 'Salud' },
    { value: 'education', label: 'Educación' },
    { value: 'shopping', label: 'Compras' },
    { value: 'other', label: 'Otros' },
  ],
  saving: [
    { value: 'emergency_fund', label: 'Fondo de emergencia' },
    { value: 'sinking_fund', label: 'Fondo para imprevistos' },
    { value: 'vacation_fund', label: 'Fondo de vacaciones' },
    { value: 'car_fund', label: 'Fondo para auto' },
    { value: 'home_fund', label: 'Fondo para casa / Prima' },
    { value: 'education_fund', label: 'Fondo educativo' },
    { value: 'wedding_fund', label: 'Fondo para boda' },
    { value: 'baby_fund', label: 'Fondo para bebé' },
    { value: 'medical_fund', label: 'Fondo médico' },
    { value: 'business_fund', label: 'Fondo para negocio' },
    { value: 'christmas_fund', label: 'Fondo navideño' },
    { value: 'general_savings', label: 'Ahorro general' },
    { value: 'other_savings', label: 'Otro ahorro' },
  ],
  investment: [
    { value: 'investment_etf', label: 'ETFs / Fondos indexados' },
    { value: 'investment_stocks', label: 'Acciones individuales' },
    { value: 'investment_bonds', label: 'Bonos' },
    { value: 'investment_mutual_funds', label: 'Fondos mutuos' },
    { value: 'investment_crypto', label: 'Criptomonedas' },
    { value: 'investment_real_estate', label: 'Bienes raíces' },
    { value: 'investment_reit', label: 'REITs (Fideicomisos inmobiliarios)' },
    { value: 'investment_retirement', label: 'Plan de retiro / Pensión voluntaria' },
    { value: 'investment_business', label: 'Inversión en negocio' },
    { value: 'investment_p2p', label: 'Préstamos P2P' },
    { value: 'investment_gold', label: 'Oro / Metales preciosos' },
    { value: 'investment_forex', label: 'Forex' },
    { value: 'investment_art', label: 'Arte / Coleccionables' },
    { value: 'investment_other', label: 'Otras inversiones' },
  ],
  debt_payment: [
    { value: 'credit_card', label: 'Tarjeta de crédito' },
    { value: 'personal_loan', label: 'Préstamo personal' },
    { value: 'mortgage', label: 'Hipoteca' },
    { value: 'car_loan', label: 'Crédito automotriz' },
    { value: 'student_loan', label: 'Crédito educativo' },
    { value: 'business_loan', label: 'Préstamo de negocio' },
    { value: 'family_loan', label: 'Préstamo familiar' },
    { value: 'medical_debt', label: 'Deuda médica' },
    { value: 'tax_debt', label: 'Deuda de impuestos' },
    { value: 'other_debt', label: 'Otras deudas' },
    { value: 'loan', label: 'Préstamo' },
  ],
};

export const transactionTypes = [
  { value: 'income', label: 'Ingreso', color: 'text-success' },
  { value: 'fixed_expense', label: 'Gasto Fijo', color: 'text-muted-foreground' },
  { value: 'variable_expense', label: 'Gasto Variable', color: 'text-warning' },
  { value: 'saving', label: 'Ahorro', color: 'text-primary' },
  { value: 'investment', label: 'Inversión', color: 'text-primary' },
  { value: 'debt_payment', label: 'Pago de Deuda', color: 'text-destructive' },
];

export const paymentMethods = [
  { value: 'cash', label: 'Efectivo' },
  { value: 'debit_card', label: 'Tarjeta de débito' },
  { value: 'credit_card', label: 'Tarjeta de crédito' },
  { value: 'transfer', label: 'Transferencia / SINPE' },
  { value: 'check', label: 'Cheque' },
  { value: 'other', label: 'Otro' },
];

export const currencies = [
  { value: 'CRC', label: 'Colones (₡)', symbol: '₡' },
  { value: 'USD', label: 'Dólares ($)', symbol: '$' },
];

export function formatCurrency(amount: number, currency: string = 'CRC'): string {
  if (currency === 'USD') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }
  
  return new Intl.NumberFormat('es-CR', {
    style: 'currency',
    currency: 'CRC',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export function formatPercentage(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}
